import React, {
  useState,
  ChangeEvent,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Button from "../../../components/Button";
import { toast } from "react-toastify";
import Title from "../../../components/Admin/Title";
import { Form, Col, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../../components/UserContext";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";

export default function UploadShort() {
  const { user } = useUser() as any;
  const token = window.localStorage.getItem("userLogged");
  const [video, setVideo] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [validated, setValidated] = useState(false);
  const [uploadResults, setUploadResults] = useState<{
    youtube?: string | boolean;
    instagram?: string | boolean;
    tiktok?: string | boolean;
  } | null>(null);

  const isAuthNotifiedRef = useRef<boolean>(false);

  const [creatorInfoLoading, setCreatorInfoLoading] = useState<boolean>(false);
  const [creatorNickname, setCreatorNickname] = useState<string | null>(null);
  const [maxDuration, setMaxDuration] = useState<number | null>(null);
  const [canPost, setCanPost] = useState<boolean>(true);
  const [postLimitMessage, setPostLimitMessage] = useState<string>("");
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [youtubeConnected, setYoutubeConnected] = useState<boolean>(true);
  const [googleAuthUrl, setGoogleAuthUrl] = useState<string | null>(null);

  const [privacyOptions, setPrivacyOptions] = useState<string[]>([]);
  const [privacyLevel, setPrivacyLevel] = useState<string>("");
  const [disableComment, setDisableComment] = useState<boolean>(false);
  const [disableDuet, setDisableDuet] = useState<boolean>(false);
  const [disableStitch, setDisableStitch] = useState<boolean>(false);
  const [commentRestricted, setCommentRestricted] = useState<boolean>(false);
  const [duetRestricted, setDuetRestricted] = useState<boolean>(false);
  const [stitchRestricted, setStitchRestricted] = useState<boolean>(false);

  const [isCommercial, setIsCommercial] = useState<boolean>(false);
  const [promoteYourBrand, setPromoteYourBrand] = useState<boolean>(false);
  const [promoteBrandedContent, setPromoteBrandedContent] =
    useState<boolean>(false);

  const BASE_URL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5500"
      : "https://ligavirtualf1.es:5500";

  const popupRef = useRef<Window | null>(null);
  const googlePopupRef = useRef<Window | null>(null); // Ref específica para Google

  const fetchCreatorInfo = useCallback(async () => {
    if (!token) return;
    setCreatorInfoLoading(true);

    try {
      const parsedToken = JSON.parse(token);
      const safeUser = encodeURIComponent(user);
      const safeToken = encodeURIComponent(parsedToken.token);

      const response = await fetch(
        `${BASE_URL}/api/admin/tiktok-creator-info?user=${safeUser}&token=${safeToken}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      const data = await response.json();

      if (response.status === 401) {
        setCanPost(false);
        setCreatorNickname(null);
        setPostLimitMessage("TikTok authentication required. Please log in.");
        if (data.authUrl) {
          setAuthUrl(data.authUrl);
        }
        return;
      }

      if (response.ok && data.success) {
        setCreatorNickname(
          data.creator_nickname || "Cuenta de TikTok vinculada",
        );
        setMaxDuration(data.max_video_post_duration_sec || 60);

        if (
          data.privacy_level_options &&
          Array.isArray(data.privacy_level_options)
        ) {
          setPrivacyOptions(data.privacy_level_options);
        } else {
          setPrivacyOptions([
            "PUBLIC_TO_EVERYONE",
            "MUTUAL_FOLLOW_FRIENDS",
            "SELF_ONLY",
          ]);
        }

        if (data.comment_disabled) setCommentRestricted(true);
        if (data.duet_disabled) setDuetRestricted(true);
        if (data.stitch_disabled) setStitchRestricted(true);

        if (data.is_creator_allowed === false || data.reach_limit) {
          setCanPost(false);
          setPostLimitMessage(
            "You have reached your posting limit at this moment. Please try again later.",
          );
        } else {
          setCanPost(true);
        }
      }
    } catch (error) {
      console.error("Error fetching creator info:", error);
    } finally {
      setCreatorInfoLoading(false);
    }
  }, [token, BASE_URL, user]);

  const checkYouTubeStatus = useCallback(async () => {
    if (!token) return;
    try {
      const parsedToken = JSON.parse(token);
      const safeUser = encodeURIComponent(user);
      const safeToken = encodeURIComponent(parsedToken.token);

      const response = await fetch(
        `${BASE_URL}/api/admin/youtube-status?user=${safeUser}&token=${safeToken}`,
      );
      const data = await response.json();

      if (!data.connected) {
        setYoutubeConnected(false);
        setGoogleAuthUrl(data.authUrl);
      } else {
        setYoutubeConnected(true);
        setGoogleAuthUrl(null);
      }
    } catch (error) {
      console.error("Error checking YouTube status:", error);
    }
  }, [token, BASE_URL, user]);

  useEffect(() => {
    fetchCreatorInfo();
    checkYouTubeStatus();
  }, [fetchCreatorInfo, checkYouTubeStatus]);

  const handleConnectTikTok = (urlToOpen?: string) => {
    const targetUrl = urlToOpen || authUrl;
    if (!targetUrl) return;

    isAuthNotifiedRef.current = false;

    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    popupRef.current = window.open(
      targetUrl,
      "TikTokAuthPopup",
      "width=600,height=700,scrollbars=yes,status=yes",
    );

    if (popupRef.current) {
      const checkPopupClosed = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkPopupClosed);
          popupRef.current = null;
          fetchCreatorInfo();
        }
      }, 1000);
    }
  };

  // Función limpia para conectar YouTube con control de cierre y toast
  const handleConnectYouTube = () => {
    if (!googleAuthUrl) return;

    if (googlePopupRef.current && !googlePopupRef.current.closed) {
      googlePopupRef.current.focus();
      return;
    }

    googlePopupRef.current = window.open(
      googleAuthUrl,
      "GoogleAuthPopup",
      "width=600,height=700,scrollbars=yes,status=yes",
    );

    if (googlePopupRef.current) {
      const checkGoogleClosed = setInterval(() => {
        if (googlePopupRef.current?.closed) {
          clearInterval(checkGoogleClosed);
          googlePopupRef.current = null;
          toast.success("YouTube conectado correctamente.");
          checkYouTubeStatus(); // Refresca el estado para cambiar a verde al instante
        }
      }, 1000);
    }
  };

  useEffect(() => {
    const notifySuccess = () => {
      if (!isAuthNotifiedRef.current) {
        isAuthNotifiedRef.current = true;
        toast.success("TikTok authentication completed successfully!");
        fetchCreatorInfo();
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "tiktok_auth_success") {
        notifySuccess();
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data === "tiktok_authenticated") {
        notifySuccess();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("message", handleMessage);
    };
  }, [fetchCreatorInfo]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const videoElement = document.createElement("video");
      videoElement.preload = "metadata";

      videoElement.onloadedmetadata = () => {
        window.URL.revokeObjectURL(videoElement.src);
        const duration = videoElement.duration;

        if (maxDuration && duration > maxDuration) {
          toast.error(
            `Video duration exceeds the maximum allowed limit of ${maxDuration} seconds.`,
          );
          setVideo(null);
          setVideoPreviewUrl(null);
          e.target.value = "";
        } else {
          setVideo(selectedFile);
          setVideoPreviewUrl(URL.createObjectURL(selectedFile));
        }
      };

      videoElement.src = URL.createObjectURL(selectedFile);
    }
  };

  const handleSubmit = async (e: any) => {
    if (token == null) return;
    e.preventDefault();

    const form = e.currentTarget;
    setValidated(true);

    if (!privacyLevel) {
      toast.error("Please select a privacy status.");
      return;
    }

    if (isCommercial && !promoteYourBrand && !promoteBrandedContent) {
      toast.error(
        "Please select at least one commercial content option (Your brand or Branded content).",
      );
      return;
    }

    if (!form.checkValidity() || !canPost) {
      return;
    }

    if (!video) {
      toast.error("Video is required");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("video", video);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("privacy_level", privacyLevel);
    formData.append("disable_comment", String(disableComment));
    formData.append("disable_duet", String(disableDuet));
    formData.append("disable_stitch", String(disableStitch));
    formData.append("is_commercial", String(isCommercial));
    formData.append("brand_organic_toggle", String(promoteYourBrand));
    formData.append("branded_content_toggle", String(promoteBrandedContent));
    formData.append("user", user);
    formData.append("token", JSON.parse(token).token);

    try {
      const response = await fetch(`${BASE_URL}/api/admin/upload-short`, {
        method: "POST",
        body: formData,
      });

      if (response.status === 401) {
        const data = await response.json();
        if (data.authUrl) {
          setAuthUrl(data.authUrl);
        }
        setCanPost(false);
        setCreatorNickname(null);
        toast.error(
          "TikTok session expired. Please connect your account again.",
        );
      } else if (response.ok) {
        const data = await response.json();

        if (data.success) {
          setUploadResults(data.details);

          toast.success("¡Proceso de subida múltiple finalizado!");
          setVideo(null);
          setVideoPreviewUrl(null);
          setTitle("");
          setDescription("");
          setPrivacyLevel("");
          setDisableComment(false);
          setDisableDuet(false);
          setDisableStitch(false);
          setIsCommercial(false);
          setPromoteYourBrand(false);
          setPromoteBrandedContent(false);
          setValidated(false);
          fetchCreatorInfo();
        } else {
          toast.error(`Error: ${data.error || "Failed to upload reels"}`);
        }
      }
    } catch (error) {
      console.error("Error connecting to the server:", error);
      toast.error("An error occurred while connecting to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Upload Reel" />
        <small className="mandatory-fields">(*) Required fields</small>

        <div className="mb-3 p-3 bg-light rounded border d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <span className="text-muted d-block">
              Target TikTok Account:{" "}
              <strong>
                {creatorInfoLoading
                  ? "Loading account info..."
                  : creatorNickname || "Not connected"}
              </strong>
            </span>
            <span
              className={`d-block small ${youtubeConnected ? "text-success" : "text-danger"}`}
            >
              YouTube:{" "}
              {youtubeConnected
                ? "Conectado"
                : "Sesión caducada o desconectado"}
            </span>
          </div>

          <div className="d-flex gap-2 align-items-center">
            {!creatorNickname && authUrl && (
              <Button
                text={"Connect TikTok"}
                onClick={() => handleConnectTikTok()}
                icon={faTiktok}
                iconPosition="left"
              />
            )}

            {!youtubeConnected && googleAuthUrl && (
              <Button
                text={"Conectar YouTube"}
                onClick={handleConnectYouTube}
              />
            )}
          </div>
        </div>

        {!canPost && <Alert variant="warning">{postLimitMessage}</Alert>}

        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Title (*)</Form.Label>
            <Form.Control
              type="text"
              required
              value={title}
              placeholder="Edit title and hashtags freely..."
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Hashtags (*)</Form.Label>
            <Form.Control
              type="text"
              required
              value={description}
              placeholder="Edit hashtags..."
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setDescription(e.target.value)
              }
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>TikTok Privacy Status (*)</Form.Label>
            <Form.Select
              required
              value={privacyLevel}
              onChange={(e) => setPrivacyLevel(e.target.value)}
            >
              <option value="">-- Select privacy status --</option>
              {privacyOptions.map((option) => {
                const isDisabledForBranded =
                  promoteBrandedContent && option === "SELF_ONLY";
                return (
                  <option
                    key={option}
                    value={option}
                    disabled={isDisabledForBranded}
                  >
                    {option === "PUBLIC_TO_EVERYONE" && "Public to everyone"}
                    {option === "MUTUAL_FOLLOW_FRIENDS" && "Friends only"}
                    {option === "SELF_ONLY" && "Only me (Private)"}
                    {![
                      "PUBLIC_TO_EVERYONE",
                      "MUTUAL_FOLLOW_FRIENDS",
                      "SELF_ONLY",
                    ].includes(option) && option}
                    {isDisabledForBranded
                      ? " (Not allowed for Branded Content)"
                      : ""}
                  </option>
                );
              })}
            </Form.Select>
            {promoteBrandedContent && (
              <small className="text-danger d-block mt-1">
                "Only me" privacy is disabled when Branded Content is selected.
              </small>
            )}
            <Form.Control.Feedback type="invalid">
              Please select a privacy status.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="form-group mb-3" as={Col} md="12">
            <Form.Label>TikTok Interaction Settings</Form.Label>
            <div className="d-flex flex-column gap-2 border p-3 rounded bg-white">
              <Form.Check
                type="checkbox"
                id="comment-check"
                label="Allow comments"
                checked={!disableComment}
                disabled={commentRestricted}
                onChange={(e) => setDisableComment(!e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="duet-check"
                label="Allow duets"
                checked={!disableDuet}
                disabled={duetRestricted}
                onChange={(e) => setDisableDuet(!e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                id="stitch-check"
                label="Allow stitch"
                checked={!disableStitch}
                disabled={stitchRestricted}
                onChange={(e) => setDisableStitch(!e.target.checked)}
              />
            </div>
          </Form.Group>

          <Form.Group
            className="form-group mb-3 border p-3 rounded bg-light"
            as={Col}
            md="12"
          >
            <Form.Check
              type="switch"
              id="commercial-content-switch"
              label="Is this commercial content?"
              checked={isCommercial}
              onChange={(e) => {
                setIsCommercial(e.target.checked);
                if (!e.target.checked) {
                  setPromoteYourBrand(false);
                  setPromoteBrandedContent(false);
                }
              }}
            />
            <small className="text-muted d-block mt-1">
              TikTok - Promote a product, brand, or yourself in the video.
            </small>

            {isCommercial && (
              <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
                <Form.Check
                  type="checkbox"
                  id="your-brand-check"
                  label="Your brand (Promoting your own business)"
                  checked={promoteYourBrand}
                  onChange={(e) => setPromoteYourBrand(e.target.checked)}
                />
                {promoteYourBrand && (
                  <small className="text-info ms-4">
                    Your photo/video will be labeled as 'Promotional content'
                  </small>
                )}

                <Form.Check
                  type="checkbox"
                  id="branded-content-check"
                  label="Branded content (Promoting a third party)"
                  checked={promoteBrandedContent}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setPromoteBrandedContent(val);
                    if (val && privacyLevel === "SELF_ONLY") {
                      setPrivacyLevel("PUBLIC_TO_EVERYONE");
                    }
                  }}
                />
                {promoteBrandedContent && (
                  <small className="text-info ms-4">
                    Your photo/video will be labeled as 'Paid partnership'
                  </small>
                )}
              </div>
            )}
          </Form.Group>

          <Form.Group className="form-group" as={Col} md="12">
            <Form.Label>Video (*)</Form.Label>
            <Form.Control
              type="file"
              accept="video/*"
              required
              onChange={handleFileChange}
              disabled={!canPost}
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
            {maxDuration && (
              <small className="text-muted d-block mt-1">
                Max video duration allowed: {maxDuration} seconds.
              </small>
            )}
          </Form.Group>

          {videoPreviewUrl && (
            <div className="mb-3 text-center border p-2 rounded bg-light">
              <p className="text-muted small mb-2">Video Preview:</p>
              <video
                src={videoPreviewUrl}
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: "250px",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}

          <div className="p-3 mb-3 bg-light border rounded text-muted small text-center">
            {promoteBrandedContent
              ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
              : "By posting, you agree to TikTok's Music Usage Confirmation."}
          </div>

          <div className="text-center">
            {loading ? (
              <div className="d-flex flex-column align-items-center gap-2">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="spinner-button fa-spin"
                />
                <small className="text-muted">
                  Uploading and processing video... Please note it may take a
                  few minutes to appear on your TikTok profile.
                </small>
              </div>
            ) : (
              <Button
                text={"Upload Video to TikTok / YT / Instagram"}
                type="submit"
                icon={faTiktok}
                iconPosition="left"
                disabled={!canPost}
              />
            )}

            {uploadResults && (
              <div className="mt-3 p-3 border rounded bg-white text-left">
                <p className="fw-bold mb-2">Estado de subida por plataforma:</p>
                <ul className="list-unstyled mb-0 small">
                  <li>
                    YouTube:{" "}
                    {uploadResults.youtube === "success" ? (
                      <span className="text-success fw-bold">
                        ✓ Subido con éxito
                      </span>
                    ) : (
                      <span className="text-danger">
                        ✗ Error: {String(uploadResults.youtube)}
                      </span>
                    )}
                  </li>
                  <li>
                    Instagram:{" "}
                    {uploadResults.instagram === "success" ? (
                      <span className="text-success fw-bold">
                        ✓ Subido con éxito
                      </span>
                    ) : (
                      <span className="text-danger">
                        ✗ Error: {String(uploadResults.instagram)}
                      </span>
                    )}
                  </li>
                  <li>
                    TikTok:{" "}
                    {uploadResults.tiktok === "success" ? (
                      <span className="text-success fw-bold">
                        ✓ Subido con éxito
                      </span>
                    ) : (
                      <span className="text-danger">
                        ✗ Error: {String(uploadResults.tiktok)}
                      </span>
                    )}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
}
